import React, { useState } from 'react';
import { FiMenu, FiPlus, FiMessageSquare, FiEdit2, FiTrash2, FiSend, FiUser } from 'react-icons/fi';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import 'bootstrap/dist/css/bootstrap.min.css';

const AIQuestionGenerator = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chats, setChats] = useState([
    { id: 1, title: 'New Chat', messages: [] }
  ]);
  const [activeChat, setActiveChat] = useState(1);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const createNewChat = () => {
    const newChat = {
      id: Date.now(),
      title: 'New Chat',
      messages: []
    };
    setChats([newChat, ...chats]);
    setActiveChat(newChat.id);
  };

  const deleteChat = (id) => {
    if (chats.length === 1) return;
    const filtered = chats.filter(chat => chat.id !== id);
    setChats(filtered);
    if (activeChat === id) {
      setActiveChat(filtered[0].id);
    }
  };

  const renameChat = (id, newTitle) => {
    setChats(chats.map(chat => 
      chat.id === id ? { ...chat, title: newTitle } : chat
    ));
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setChats(chats.map(chat => {
      if (chat.id === activeChat) {
        const updatedMessages = [...chat.messages, userMessage];
        const newTitle = chat.messages.length === 0 
          ? inputMessage.slice(0, 30) + (inputMessage.length > 30 ? '...' : '')
          : chat.title;
        return { ...chat, messages: updatedMessages, title: newTitle };
      }
      return chat;
    }));

    setInputMessage('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiMessage = {
        id: Date.now() + 1,
        text: generateAIResponse(userMessage.text),
        sender: 'ai',
        timestamp: new Date()
      };

      setChats(chats.map(chat => {
        if (chat.id === activeChat) {
          return { ...chat, messages: [...chat.messages, userMessage, aiMessage] };
        }
        return chat;
      }));
      setIsTyping(false);
    }, 1500);
  };

  const generateAIResponse = (userInput) => {
    const responses = [
      `Great question! Here's what I can tell you about "${userInput}": This is a thoughtful inquiry that requires careful consideration. Let me break it down for you with detailed insights.`,
      `I understand you're asking about "${userInput}". Here's a comprehensive response: Based on the context provided, I can offer several perspectives on this topic.`,
      `Thank you for your question regarding "${userInput}". Let me provide you with a detailed answer that covers the key aspects of your inquiry.`
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const currentChat = chats.find(chat => chat.id === activeChat);

  return (
    <div style={{ display: 'flex', height: '90vh', background: '#f8f9fa', overflow :'hidden',position: 'relative', marginTop:'90px'}}>
      {/* Sidebar */}
      <div 
        style={{
          width: sidebarCollapsed ? '60px' : '260px',
          background: '#212529',
          color: 'white',
          transition: 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #495057'
        }}
      >
        {/* Sidebar Header */}
        <div style={{ padding: '15px', borderBottom: '1px solid #495057' }}>
          <button 
            className="btn btn-dark w-100 d-flex align-items-center justify-content-center"
            onClick={createNewChat}
            style={{ gap: '8px' }}
          >
            <FiPlus size={20} />
            {!sidebarCollapsed && <span>New Chat</span>}
          </button>
        </div>

        {/* Chat List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
          {chats.map(chat => (
            <div
              key={chat.id}
              onClick={() => setActiveChat(chat.id)}
              style={{
                padding: sidebarCollapsed ? '12px 8px' : '12px',
                marginBottom: '5px',
                background: activeChat === chat.id ? '#495057' : 'transparent',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                if (activeChat !== chat.id) e.currentTarget.style.background = '#343a40';
              }}
              onMouseLeave={(e) => {
                if (activeChat !== chat.id) e.currentTarget.style.background = 'transparent';
              }}
            >
              <FiMessageSquare size={18} style={{ flexShrink: 0 }} />
              {!sidebarCollapsed && (
                <>
                  <span style={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    fontSize: '14px'
                  }}>
                    {chat.title}
                  </span>
                  {chats.length > 1 && (
                    <button
                      className="btn btn-sm btn-link p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteChat(chat.id);
                      }}
                      style={{ color: '#6c757d' }}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Toggle Button */}
        <div style={{ padding: '15px', borderTop: '1px solid #495057' }}>
          <button 
            className="btn btn-dark w-100 d-flex align-items-center justify-content-center"
            onClick={toggleSidebar}
          >
            {sidebarCollapsed ? <BiChevronRight size={20} /> : <BiChevronLeft size={20} />}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div 
          style={{
            padding: '15px 20px',
            background: 'white',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h5 style={{ margin: 0, fontWeight: 600 }}>AI Question Generator</h5>
          <div className="d-flex align-items-center gap-2">
            <FiUser size={24} style={{ color: '#6c757d' }} />
          </div>
        </div>

        {/* Chat Messages */}
        <div 
          style={{ 
            flex: 1, 
            overflowY: 'auto', 
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}
        >
          {currentChat?.messages.length === 0 ? (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              color: '#6c757d'
            }}>
              <FiMessageSquare size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
              <h4>Start a new conversation</h4>
              <p>Ask me anything and I'll help generate questions and answers</p>
            </div>
          ) : (
            <>
              {currentChat?.messages.map(message => (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    gap: '15px',
                    padding: '15px',
                    background: message.sender === 'user' ? '#e3f2fd' : 'white',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: message.sender === 'user' ? '#bbdefb' : '#dee2e6'
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: message.sender === 'user' ? '#2196f3' : '#6c757d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {message.sender === 'user' ? <FiUser size={18} /> : 'AI'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, lineHeight: '1.6' }}>{message.text}</p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div style={{
                  display: 'flex',
                  gap: '15px',
                  padding: '15px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: '#6c757d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    AI
                  </div>
                  <div style={{ flex: 1 }}>
                    <span>Typing...</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Input Area */}
        <div style={{ 
          padding: '20px', 
          background: 'white', 
          borderTop: '1px solid #dee2e6' 
        }}>
          <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto',
            display: 'flex',
            gap: '10px',
            alignItems: 'end'
          }}>
            <textarea
              className="form-control"
              placeholder="Type your message here..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
              style={{ 
                resize: 'none',
                minHeight: '50px',
                maxHeight: '150px',
                borderRadius: '8px'
              }}
            />
            <button 
              className="btn btn-primary"
              onClick={sendMessage}
              disabled={!inputMessage.trim()}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FiSend size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIQuestionGenerator;